import { Campaign, GetCampaignParams } from '@features/campaigns';
import { useCurrentUser } from '@features/users';
import {
  buildPresentation,
  useRslLciReportFields,
} from '@features/lci-report/subdomains/rsl';
import type { DesignVersion } from '@features/lci-report/subdomains/rsl';
import { RslReportsParams } from '@features/campaigns/subdomains/rsl';
import { clientDefaultValues } from '@features/lci-report/subdomains/rsl/utils/client-default-values';
import { LciReportDownloadButton } from './LciReportDownloadButton';
import { presentationToFile } from '@features/powerpoint';
import { stringify } from 'qs';
import { useNavigate } from 'react-router-dom';
import { useFeatures } from '@utils/hooks';

export type RslLciReportDownloadButtonProps = {
  campaignId: Campaign['id'];
  campaignParams: Omit<GetCampaignParams, 'id'>;
  reportParams: Omit<RslReportsParams, 'campaignId'>;
};

export const RslLciReportDownloadContainer = (
  props: RslLciReportDownloadButtonProps
) => {
  const navigate = useNavigate();
  const { role: userRole } = useCurrentUser();
  const { pptRedesign } = useFeatures();
  const designVersion: DesignVersion = pptRedesign ? 'v2' : 'v1';

  const { data, loading } = useRslLciReportFields({
    campaignParams: {
      ...props.campaignParams,
      id: props.campaignId,
    },
    reportParams: props.reportParams,
  });

  if (!loading && !data) {
    console.error('RslLciReportDownloadContainer: Failed to load report data', {
      campaignId: props.campaignId,
      campaignParams: props.campaignParams,
      reportParams: props.reportParams,
    });
    return null;
  }

  const handleDownload = () => {
    if (!data) throw new Error('Missing data for presentation');

    const presentation = buildPresentation({
      campaign: data.campaign,
      selectedReportDate: data.selectedReportDate,
      brandLogo: null,
      salesSummary: data.salesSummary,
      visitMetrics: data.visitMetrics,
      subdimensions: data.subdimensions,
      dimensionTableData: data.dimensionTableData,
      visitDimensionTableData: data.visitDimensionTableData,
      campaignAnalyticsReport: data.campaignAnalyticsReport,
      calculatedFields: data.calculatedFields,
      report: data.report,
      customFields: clientDefaultValues(data.report, data.campaign.name),
      designVersion,
    });

    if (userRole === 'user') {
      void presentationToFile(presentation);
      return;
    }

    void navigate(
      `/campaigns/rsl/${props.campaignId}/lci-report?${stringify({ ...props.campaignParams, ...props.reportParams })}`
    );
  };

  return (
    <LciReportDownloadButton
      loading={loading}
      userRole={userRole}
      onDownload={handleDownload}
      startIcon={loading ? '...' : undefined}
    />
  );
};
